/**
 * Cargo check for stops WITHOUT a manifest (no manifest lines on the stop).
 *
 * At a pickup/drop stop with no manifest lines the crew must, after Start servicing and
 * before Mark serviced, either confirm the piece count (`piece_count`, when an expected count
 * is known for the stop) or confirm that the person in charge (PIC) at the site checked that
 * all cargo was loaded/unloaded (`pic_confirmation`). The mode is derived from the booking:
 *
 *  1. `trip_stop.expected_pieces` when set (nothing sets it yet — reserved for per-stop counts);
 *  2. else the trip's `declaredPieceCount` (> 0) applies to the pickup when the trip has
 *     exactly one pickup, and to the drop only when the trip has exactly one drop;
 *  3. otherwise there is no expected count → `pic_confirmation`.
 *
 * Shared by the API (gates, DTOs) and the web crew app; Android ports these rules.
 */
export const CARGO_CHECK_MODES = ['piece_count', 'pic_confirmation'];
export const CARGO_MISMATCH_REASONS = ['short', 'extra', 'damaged', 'other'];
export const CARGO_MISMATCH_REASON_LABELS = {
    short: 'Short (missing pieces)',
    extra: 'Extra pieces',
    damaged: 'Damaged',
    other: 'Other',
};
/** Expected piece count for a stop (null = unknown → PIC confirmation). */
export function expectedPiecesForStop(stop, allStops, declaredPieceCount) {
    if (stop.type !== 'pickup' && stop.type !== 'drop')
        return null;
    if (typeof stop.expectedPieces === 'number' && stop.expectedPieces >= 0)
        return stop.expectedPieces;
    if (typeof declaredPieceCount !== 'number' || declaredPieceCount <= 0)
        return null;
    const sameType = allStops.filter((s) => s.type === stop.type);
    return sameType.length === 1 ? declaredPieceCount : null;
}
export function cargoCheckModeFor(expectedPieces) {
    return expectedPieces == null ? 'pic_confirmation' : 'piece_count';
}
/** A cargo check applies to pickup/drop stops that have no manifest lines. */
export function cargoCheckApplies(stopType, manifestLineCount) {
    return (stopType === 'pickup' || stopType === 'drop') && manifestLineCount === 0;
}
// ── Copy (web + Android) ─────────────────────────────────────────────────────
export const CARGO_CHECK_END_SERVICE_BLOCK_MESSAGE = 'Confirm the cargo (piece count or PIC confirmation) before marking this stop serviced.';
export const CARGO_CHECK_DEPART_BLOCK_MESSAGE = 'Confirm the cargo (piece count or PIC confirmation) before departing this stop, or file an exception to proceed.';
/** One-line description for the POD PDF / dispatcher view. */
export function describeCargoCheck(c) {
    const pic = c.picName?.trim() || 'the PIC';
    if (c.mode === 'piece_count') {
        const counted = c.countedPieces ?? 0;
        const expected = c.expectedPieces;
        const of = expected == null ? `${counted}` : `${counted} of ${expected}`;
        const base = `${of} piece${(expected ?? counted) === 1 ? '' : 's'} confirmed with ${pic} (PIC)`;
        if (expected != null && counted !== expected) {
            const label = c.mismatchReason
                ? CARGO_MISMATCH_REASON_LABELS[c.mismatchReason] ?? c.mismatchReason
                : 'count differs';
            return `${base} — mismatch: ${label}`;
        }
        return base;
    }
    const verb = c.stopType === 'drop' ? 'unloaded' : 'loaded';
    return `PIC ${pic} confirmed all cargo ${verb}`;
}
